import {
  Box,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Container,
} from "@mui/material";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";

const OfferCountdown = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Initialize with 24 hours from first visit
  const getInitialTime = () => {
    const storedEndTime = localStorage.getItem("offerEndTime");
    if (storedEndTime) {
      return parseInt(storedEndTime);
    }

    const endTime = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("offerEndTime", endTime.toString());
    return endTime;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const endTime = getInitialTime();
    const difference = endTime - new Date().getTime();

    if (difference <= 0) {
      // Reset timer if expired
      localStorage.removeItem("offerEndTime");
      const newEndTime = new Date().getTime() + 24 * 60 * 60 * 1000;
      localStorage.setItem("offerEndTime", newEndTime.toString());
      return { hours: 23, minutes: 59, seconds: 59 };
    }

    return {
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearTimeout(timer);
  });

  const handleBookNow = () => {
    // Scroll to contact form or navigate to booking page
    const contactSection = document.getElementById("contact-form");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const TimeUnit = ({ value, label }) => (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
      key={`${value}-${label}`}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minWidth: isSmallMobile ? 70 : 100,
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 3,
            padding: isSmallMobile ? "16px 12px" : "20px 16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            minWidth: isSmallMobile ? 65 : 90,
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            },
          }}
        >
          <Typography
            variant={isSmallMobile ? "h4" : "h3"}
            sx={{
              fontWeight: 800,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.error.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              lineHeight: 1.2,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            {value.toString().padStart(2, "0")}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "white",
            fontWeight: 600,
            marginTop: 2,
            textTransform: "uppercase",
            letterSpacing: 1,
            fontSize: isSmallMobile ? "0.8rem" : "0.9rem",
          }}
        >
          {label}
        </Typography>
      </Box>
    </motion.div>
  );

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Animated background elements */}
      <motion.div
        style={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        style={{
          position: "absolute",
          bottom: "20%",
          right: "8%",
          width: 80,
          height: 80,
          borderRadius: "30%",
          background: "rgba(255,255,255,0.08)",
          rotate: 45,
        }}
        animate={{
          scale: [1, 1.5, 1],
          rotate: [45, 90, 45],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      <motion.div
        style={{
          position: "absolute",
          top: "60%",
          left: "10%",
          width: 60,
          height: 60,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
        }}
        animate={{
          y: [0, -20, 0],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />

      <Container>
        <Grid
          container
          spacing={4}
          alignItems="center"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Content Section */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: "white",
                  fontWeight: 800,
                  marginBottom: 2,
                  fontSize: { xs: "2.2rem", md: "3rem" },
                }}
              >
                Limited Time Offer!🎉
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  marginBottom: 3,
                  fontWeight: 500,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                Get{" "}
                <Box
                  component="span"
                  sx={{ color: "#FFD700", fontWeight: 700 }}
                >
                  20% OFF
                </Box>{" "}
                your first cleaning service
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  marginBottom: 4,
                  lineHeight: 1.7,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  width: { xs: "100%", md: "40%" },
                }}
              >
                Don't miss this exclusive opportunity to experience premium
                cleaning services at an incredible discount. Our professional
                team is ready to transform your space!
              </Typography>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  onClick={handleBookNow}
                  size="large"
                  sx={{
                    backgroundColor: "white",
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    padding: { xs: "14px 32px", md: "16px 40px" },
                    borderRadius: 3,
                    textTransform: "none",
                    boxShadow: "0 8px 32px rgba(255,255,255,0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.9)",
                      boxShadow: "0 12px 40px rgba(255,255,255,0.4)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  🎁 Claim Your 20% Discount Now!
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    display: "block",
                    marginTop: 2,
                    fontSize: "0.8rem",
                  }}
                >
                  ⚡ Limited spots available • 🎯 New customers only • 💯
                  Satisfaction guaranteed
                </Typography>
              </motion.div>
            </motion.div>
          </Grid>

          {/* Countdown Section */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Box
                sx={{ textAlign: { xs: "center", md: "right" } }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    marginBottom: 3,
                    textTransform: "uppercase",
                    letterSpacing: 2,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    alignSelf: "center",
                  }}
                >
                  Offer Expires In:
                </Typography>

                <Box sx={{ marginBottom: 4 }}>
                  <Grid
                    container
                    spacing={isSmallMobile ? 2 : 3}
                    justifyContent={{ xs: "center", md: "flex-end" }}
                  >
                    <Grid item>
                      <TimeUnit value={timeLeft.hours} label="Hours" />
                    </Grid>
                    <Grid item>
                      <Box
                        sx={{
                          color: "white",
                          fontSize: { xs: "2rem", md: "2.5rem" },
                          marginTop: 2,
                          fontWeight: 700,
                        }}
                      >
                        :
                      </Box>
                    </Grid>
                    <Grid item>
                      <TimeUnit value={timeLeft.minutes} label="Minutes" />
                    </Grid>
                    <Grid item>
                      <Box
                        sx={{
                          color: "white",
                          fontSize: { xs: "2rem", md: "2.5rem" },
                          marginTop: 2,
                          fontWeight: 700,
                        }}
                      >
                        :
                      </Box>
                    </Grid>
                    <Grid item>
                      <TimeUnit value={timeLeft.seconds} label="Seconds" />
                    </Grid>
                  </Grid>
                </Box>

                {/* Progress Bar */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: 6,
                      backgroundColor: "rgba(255,255,255,0.2)",
                      borderRadius: 3,
                      overflow: "hidden",
                      marginBottom: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                        transformOrigin: "left",
                      }}
                    />
                  </Box>
                </motion.div>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontStyle: "italic",
                  }}
                >
                  ⏰ Time is running out! Book now to secure your discount
                </Typography>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default OfferCountdown;
